import { FormField } from "@/components/forms/FormField.tsx";
import type { ResourceType } from "@/modules/api/resourceTypes.tsx";
import { LimitedBillingAccountNoticeLoader } from "@/modules/billing/LimitedBillingAccountNotice.tsx";
import { BillingSelect, type BillingSelectLoaderProps } from "@/modules/billing/forms/BillingSelect.tsx";
import { Trans, useLingui } from "@lingui/react/macro";
import { Suspense, type ReactNode } from "react";
import { Controller, useFormContext } from "react-hook-form";

export interface BillingAccountInput {
    billing_account_id?: number;
}

export interface BillingAccountFieldProps extends Omit<BillingSelectLoaderProps, "onChange"> {
    description?: ReactNode;
    resourceType: ResourceType | undefined;
}

function isLimitedResourceType(resourceType: ResourceType) {
    return resourceType === "virtual_machine" || resourceType === "managed_service" || resourceType === "bare_metal";
}

const defaultDescription = (
    <Trans>
        Choose a billing account for your new resource to consolidate your resources under a suitable payment method.
    </Trans>
);

export function BillingAccountField({ description = defaultDescription, ...props }: BillingAccountFieldProps) {
    const { t } = useLingui();
    const { control } = useFormContext<BillingAccountInput>();
    const { isRequired, resourceType } = props;

    return (
        <Controller
            control={control}
            name="billing_account_id"
            render={({ field }) => (
                <>
                    <FormField isRequired={isRequired} label={t`Billing account`} description={description}>
                        <BillingSelect
                            isDisabled={field.disabled}
                            valueKey={field.value ?? null}
                            onChange={(account) => field.onChange(account.id)}
                            {...props}
                        />
                    </FormField>

                    {resourceType != null && isLimitedResourceType(resourceType) && field.value && (
                        <FormField wide>
                            <Suspense>
                                <LimitedBillingAccountNoticeLoader baId={field.value} />
                            </Suspense>
                        </FormField>
                    )}
                </>
            )}
        />
    );
}
